/**
 * `appStore.toolbarCustomization` — the user's own toolbar, and the anchor
 * policy that keeps a stored one legal.
 *
 * SHAPE. `{ version, primary, hidden } | null`, exactly what F01-P3 specifies.
 * `null` means "follow the shipped defaults forever", so a later default
 * reshuffle still reaches everyone who never customized. The user authors ONLY
 * the primary row: the More ▾ menu is derived from `DEFAULT_TOOLBAR_LAYOUT`
 * minus whatever was promoted or hidden, which is why `more` is not persisted
 * and a newly-shipped menu item appears for customizers too.
 *
 * ANCHOR POLICY — REPAIR, NOT REJECT (the decision B11 §10.3 left to B14).
 * A stored layout that breaks an anchor is normalized back into a legal one
 * and the repair is reported, rather than the whole customization being
 * discarded. Three reasons:
 *   1. The correct repair is unique and derivable (Home first, More ▾ last,
 *      neither hidden) — repair is only the wrong answer when the intent is
 *      ambiguous, and here it is not.
 *   2. Rejection turns one bad line in `localStorage` — or one stale field in
 *      a bundle imported from a newer build — into "my toolbar keeps
 *      resetting", the exact failure mode B11 warned about.
 *   3. It matches the precedent already set on this branch: the settings
 *      importer rejects only genuinely ambiguous corruption (one id on both
 *      surfaces) and REPAIRS the anchor case, dropping `home`/`more` out of
 *      `hidden` with a `toolbar.unhideable` warning.
 * The user is told either way — see `TOOLBAR_REPAIR_MESSAGES`, surfaced by the
 * Settings → UI toolbar panel next to Reset.
 */
import {
  DEFAULT_TOOLBAR_LAYOUT,
  TOOLBAR_ANCHOR_FIRST_ID,
  TOOLBAR_ANCHOR_LAST_ID,
  UNHIDEABLE_TOOLBAR_ITEM_IDS,
  type ToolbarItemId,
  type ToolbarLayout,
  type ToolbarMoreGroupId,
} from './toolbarItemTypes';
import { mergeToolbarLayout } from './mergeToolbarLayout';

export const TOOLBAR_CUSTOMIZATION_VERSION = 1;

/**
 * Primary-row budget (F01-P3 / D6). A Settings-UI guardrail rather than a data
 * invariant: `normalizeToolbarCustomization` never truncates a longer row,
 * because silently deleting buttons out of an imported layout would be worse
 * than a long row that the responsive toolbar already collapses.
 *
 * It must stay ABOVE `DEFAULT_TOOLBAR_LAYOUT.primary.length`, not equal to it:
 * a cap the shipped default already sits on makes every promotion refuse on a
 * fresh install, so the customization surface would arrive dead. The original
 * 9 was two slots of headroom over a 7-item default; `visual-query` and
 * `dashboards` took both, so the cap moved to 11. `visual-query` has since
 * moved to More ▾ (the Query Editor toolbar has its own Visual button), which
 * only widens the headroom.
 */
export const TOOLBAR_PRIMARY_CAP = 11;

export type ToolbarCustomization = {
  version: number;
  /** The primary row, in order. Always includes both anchors. */
  primary: ToolbarItemId[];
  /** Ids removed from the toolbar entirely — the row AND the More menu. */
  hidden: ToolbarItemId[];
};

export type ToolbarRepairCode =
  | 'anchor-first'
  | 'anchor-last'
  | 'anchor-unhidden'
  | 'duplicate'
  | 'unknown-id';

export const TOOLBAR_REPAIR_MESSAGES: Record<ToolbarRepairCode, string> = {
  'anchor-first': 'Home was moved back to the start of the toolbar — it always stays first.',
  'anchor-last': 'More ▾ was moved back to the end of the toolbar — it always stays last.',
  'anchor-unhidden':
    'Home and More ▾ can’t be hidden (hiding More ▾ would strand everything inside it), so they were put back.',
  duplicate: 'A button was listed more than once and the extra entry was dropped.',
  'unknown-id': 'Buttons from a different version of Jam SQL Studio were skipped.',
};

const KNOWN_IDS = new Set<string>([
  ...DEFAULT_TOOLBAR_LAYOUT.primary,
  ...DEFAULT_TOOLBAR_LAYOUT.more.flatMap((group) => group.items),
]);
const UNHIDEABLE = new Set<string>(UNHIDEABLE_TOOLBAR_ITEM_IDS);

type Repairs = Set<ToolbarRepairCode>;

/** Keeps known ids only, first occurrence wins. Reports what it dropped. */
function cleanIds(value: unknown, seen: Set<string>, repairs: Repairs): ToolbarItemId[] {
  if (!Array.isArray(value)) return [];
  const out: ToolbarItemId[] = [];
  for (const id of value) {
    if (typeof id !== 'string' || !KNOWN_IDS.has(id)) {
      repairs.add('unknown-id');
      continue;
    }
    if (seen.has(id)) {
      repairs.add('duplicate');
      continue;
    }
    seen.add(id);
    out.push(id as ToolbarItemId);
  }
  return out;
}

/** Moves `id` to `edge`, inserting it when a stored layout dropped it. */
function anchor(
  ids: ToolbarItemId[],
  id: ToolbarItemId,
  edge: 'first' | 'last',
  code: ToolbarRepairCode,
  repairs: Repairs,
): ToolbarItemId[] {
  const at = edge === 'first' ? 0 : ids.length - 1;
  if (ids[at] === id) return ids;
  repairs.add(code);
  const rest = ids.filter((other) => other !== id);
  return edge === 'first' ? [id, ...rest] : [...rest, id];
}

export function normalizeToolbarCustomization(value: unknown): {
  customization: ToolbarCustomization | null;
  repairs: ToolbarRepairCode[];
} {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { customization: null, repairs: [] };
  }
  const raw = value as { primary?: unknown; hidden?: unknown };
  if (!Array.isArray(raw.primary)) return { customization: null, repairs: [] };

  const repairs: Repairs = new Set();
  // `hidden` is read FIRST so an id listed as both shown and hidden resolves
  // the way the renderer resolves it: hidden wins.
  const seen = new Set<string>();
  const hidden = cleanIds(raw.hidden, seen, repairs).filter((id) => {
    if (!UNHIDEABLE.has(id)) return true;
    repairs.add('anchor-unhidden');
    seen.delete(id);
    return false;
  });
  const rawPrimary = cleanIds(raw.primary, seen, repairs);

  let primary = anchor(rawPrimary, TOOLBAR_ANCHOR_FIRST_ID, 'first', 'anchor-first', repairs);
  primary = anchor(primary, TOOLBAR_ANCHOR_LAST_ID, 'last', 'anchor-last', repairs);

  // "Is this the default?" is asked of the row the user will SEE, not of the
  // stored array — un-hiding the last hidden button leaves a row that merges
  // back to the shipped default even though the array it came from is sparse.
  const effective = mergeToolbarLayout({ primary, more: [], hidden }).primary;
  const isDefault =
    hidden.length === 0 &&
    effective.length === DEFAULT_TOOLBAR_LAYOUT.primary.length &&
    effective.every((id, index) => id === DEFAULT_TOOLBAR_LAYOUT.primary[index]);

  return {
    // A layout that reproduces the shipped default is stored as "no
    // customization", so rearranging by hand leaves the user no more frozen
    // out of a future default change than pressing Reset would.
    customization: isDefault
      ? null
      : { version: TOOLBAR_CUSTOMIZATION_VERSION, primary, hidden },
    repairs: [...repairs],
  };
}

/**
 * The `ToolbarLayout` the renderer consumes. `more: []` = derive it.
 *
 * Normalizes first, and that is not belt-and-braces: this is the ONLY thing
 * standing between a persisted value and the render tree. `mergeToolbarLayout`
 * assumes `primary`/`hidden` are arrays, so a hand-edited
 * `{ primary: 'home' }` would throw into the top-level error boundary — a
 * screen with no Settings and no Reset on it, leaving `localStorage` surgery
 * as the only way out. The Settings panel survives that value; the user just
 * can't reach it.
 */
export function toolbarLayoutFromCustomization(
  customization: ToolbarCustomization | null,
): ToolbarLayout {
  const { customization: safe } = normalizeToolbarCustomization(customization);
  if (!safe) return DEFAULT_TOOLBAR_LAYOUT;
  return { primary: safe.primary, more: [], hidden: safe.hidden };
}

export type ToolbarBuckets = {
  primary: ToolbarItemId[];
  more: Array<{ group: ToolbarMoreGroupId; items: ToolbarItemId[] }>;
  hidden: ToolbarItemId[];
};

/**
 * What the toolbar will actually look like — the same merge the renderer runs,
 * so the Settings panel and the toolbar can never disagree.
 */
export function resolveToolbarBuckets(
  customization: ToolbarCustomization | null,
): ToolbarBuckets {
  // Normalized here as well as inside `toolbarLayoutFromCustomization` because
  // `hidden` is read off the customization directly: the Settings panel
  // renders a row per hidden id and must never offer to restore an anchor it
  // was never allowed to hide in the first place.
  const { customization: safe } = normalizeToolbarCustomization(customization);
  const merged = mergeToolbarLayout(toolbarLayoutFromCustomization(safe));
  return { primary: merged.primary, more: merged.more, hidden: safe?.hidden ?? [] };
}
